import { useMemo, type ReactElement } from 'react'
import { useCartItemContext } from './context/useCartItemContext'

/**
 * Builds the UOM string
 */
function buildUomString (uom?: string): string {
  if (uom == null) {
    return ''
  }

  switch (uom) {
    case 'INCH':
      return 'in'
    case 'MILLIMETER':
      return 'mm'
    default:
      return uom
  }
}

/**
 * Component that will show the dimensions of a cart item
 */
function CartItemDimensions (): ReactElement | null {
  const { cartLine } = useCartItemContext()

  /**
   * Builds the dimensions string
   */
  const dimensionsString = useMemo(() => {
    // Dimension 1 is length if it exists, otherwise it is width
    const dimension1 = cartLine.options?.length?.value ?? cartLine.options?.width?.value
    const uom1 = cartLine.options?.length?.uom ?? cartLine.options?.width?.uom

    // Only use dimension 2 as width if there is a valid length value
    const dimension2 = cartLine.options?.length?.value != null ? cartLine.options.width?.value : null
    const uom2 = cartLine.options?.length?.uom != null ? cartLine.options.width?.uom : undefined

    if (dimension1 != null || dimension2 != null) {
      const dimension1String = `${dimension1} ${buildUomString(uom1)}`
      const dimension2String = dimension2 != null ? ` x ${dimension2} ${buildUomString(uom2)}` : ''
      // Only show '(L x W)' if both dimensions are present
      const dimensionsLayoutString = (dimension1 != null && dimension2 != null) ? ' (L x W)' : ''

      return `${dimension1String}${dimension2String}${dimensionsLayoutString}`
    }

    return null
  }, [cartLine.options?.length, cartLine.options?.width])

  if (dimensionsString == null) {
    return null
  }

  return (
    <p className='mt-3 mb-0 d-print-none'>
      <b>Dimensions:</b> {dimensionsString}
    </p>
  )
}

export {
  CartItemDimensions
}
